import { useEffect, useState, useRef } from "react";
import { FiPlus, FiTrash, FiX, FiArrowLeft, FiHelpCircle, FiCheck, FiAlertCircle, FiFileText } from "react-icons/fi";
import { Document, Page } from './LazyPDF';
import { CreatePDFFromTemplateAndCSV } from "../utilities/calls";
import FloatingIcon from "./FloatingIcon";

const emptyRow = () => ({
  Page: "1",
  Type: "TEXT",
  X: "",
  Y: "",
  Value: "",
  FontSize: "12",
  ImagePath: "",
  TEXTCOLOR: "#000000",
  Scale: "1.0"
});
// Reusable Approval Action Component for Mobile/Desktop positioning
const ApprovalControls = ({ isMobile = false, filename, updateFileName, canApprove, handleApprove }) => (
  <div className={`flex items-center gap-3 ${isMobile ? "p-4 bg-gray-900 border-t border-gray-800" : ""}`}>
    <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded px-2 py-1.5 focus-within:border-teal-500 transition-colors flex-1 sm:flex-none">
      <FiFileText className="text-gray-500" />
      <input
        type="text"
        placeholder="Filename..."
        value={filename}
        onChange={(e) => {updateFileName(e.target.value)}}
        className="bg-transparent text-sm text-white outline-none w-full sm:w-48"
      />
    </div>
    <button
      onClick={handleApprove}
      disabled={!canApprove}
      className={`p-2 rounded text-sm font-bold transition-all flex items-center justify-center gap-2 ${canApprove ? "bg-green-600 text-white hover:bg-green-500 shadow-lg" : "bg-gray-700 text-gray-500 cursor-not-allowed"}`}
    >
      Approve & Generate
    </button>
  </div>
);

export default function CSVTemplateEditor({ onCancel, onClosePreview, providerId, storeId, fileId, defaultFilename }) {
  const [numPages, setNumPages] = useState(null);
  const [rows, setRows] = useState([]);
  const [generatedUrl, setGeneratedUrl] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [viewMode, setViewMode] = useState('edit');
  const [hasPreviewed, setHasPreviewed] = useState(false);
  const [fileName, setFileName] = useState(defaultFilename || "template.pdf");
  const [successMessage, setSuccessMessage] = useState();
  const [currXY, setCurrXY] = useState({X : 10, Y : 10});
  const pageRefs = useRef({});
  const [pageDIMs, setPageDIMs] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const bottomspanspace = 8;

  // Function to scroll to a specific page number
  const scrollToPage = (pageNumber) => {
    if (pageRefs.current[pageNumber]) {
      setCurrentPage(pageNumber);
      pageRefs.current[pageNumber].scrollIntoView({ behavior: 'smooth' });
    } else {
      setCurrentPage(0); 
    }
  };

  useEffect(() => {
    handlePreviewTemplate();
  },[]); 

  const renderCrossHair = (page) => {
    if (page === parseInt(currentPage, 10)) {
        return <FloatingIcon x={currXY.X} y={currXY.Y} size={50} color={"red"} />
    } else {
        return <></>;
    }
  };

  const handlePageLoadSuccess = (pageData, pagenumber) => {   
    setPageDIMs((pList) => ({...pList, [pagenumber] : {xscale : pageData.width / pageData.originalWidth, yscale: pageData.height / pageData.originalHeight, width: pageData.width, height : pageData.height}}))
  };

  const XPage = (pageNumber, val, type = "dpi") => {
   let scale = 1;
     if (pageRefs.current[pageNumber]) {
      scale = pageDIMs[pageNumber].xscale;
    }
    let xdp = type === "mm" ? (scale * val * 72 / 25.4): (val * scale ) ;
    return Math.floor(xdp);
  };
  const YPage = (pageNumber, val, type = "dpi") => {
    let scale = 1;
    let ydp = 0;
    if (pageRefs.current[pageNumber]) {
      let pageH = pageDIMs[pageNumber].height;
      scale = pageDIMs[pageNumber].yscale;
      ydp = pageH - (type === "dpi" ? val : val * 72 / 25.4);
      ydp = ydp * scale;
      ydp = ydp + pageRefs.current[pageNumber].getBoundingClientRect().top - bottomspanspace;;
    }
    
    return Math.floor(ydp);
  };

  const isRowValid = (row) => {
    const common = row.Page && row.X && row.Y;
    if (row.Type === "TEXT") {
      return !!(common && row.Value !== "" && row.FontSize && row.TEXTCOLOR);
    }
    if (row.Type === "IMAGE") {
      return !!(common && row.ImagePath !== "" && row.Scale);
    }
    return false;
  };

  const isFormValid = rows.length === 0 || ( rows.length > 0 && rows.every(isRowValid));
  const canApprove = hasPreviewed && isFormValid && fileName.trim() !== "";

  const invalidatePreview = () => setHasPreviewed(false);

  const updateCell = (rowIndex, key, value) => {
    const updated = [...rows];
    updated[rowIndex][key] = value;
    setRows(updated);
    invalidatePreview();
  };

  const addRow = () => {
    setRows([...rows, emptyRow()]);
    invalidatePreview();
  };

  const removeRow = (rowIndex) => {
    const updated = rows.filter((_, i) => i !== rowIndex);
    setRows(updated.length ? updated : []);
    invalidatePreview();
  };
  const buildCSV = (rows) => {
    const header = ["Page", "Type", "X", "Y", "Value", "FontSize", "ImagePath", "TEXTCOLOR", "Scale"];
    const body = rows.map(row => {
        const escapedValue = String(row.Value ?? '').replace(/"/g, '""');
        return [
          row.Page, row.Type, row.X, row.Y, `"${escapedValue}"`, row.FontSize, row.ImagePath, row.TEXTCOLOR, row.Scale
        ].join(",");
    });
    return [header.join(","), ...body].join("\n");
  };

  const handlePreviewTemplate = async () => {
    if (!isFormValid) return;
    const csvText = buildCSV(rows);
    let providerJRN = "jrn:serviceprovider:" + providerId;
    try {
      const blob = await CreatePDFFromTemplateAndCSV(providerJRN, storeId, fileId, csvText, fileName || "preview.pdf", true);
      const url = URL.createObjectURL(blob);
      setGeneratedUrl(url);
      setHasPreviewed(true);
      pageRefs.current = {};
      if (window.innerWidth < 1024) setViewMode('preview');
    } catch (err) {
      console.error("Preview failed", err);
    }
  };

  const handleApprove = async () => {
    try {
      const csvText = buildCSV(rows);
      let providerJRN = "jrn:serviceprovider:" + providerId;
      const result = await CreatePDFFromTemplateAndCSV(
        providerJRN,
        storeId,
        fileId,
        csvText,
        fileName,
        false
      );
      console.log("Created file:", result.fileId);
      setSuccessMessage(`Document "${fileName}" generated successfully.`);
      window.scrollTo(0, 0);
      // return to browser after short delay
      setTimeout(() => {
        setSuccessMessage(null);
        onClosePreview();
      }, 3000);

    } catch (err) {
      let message = "Failed to generate document.";
      if (err?.response?.data?.message) {
        message = err.response.data.message;
      } else if (err?.message) {
        message = err.message;
      }
      setSuccessMessage(message);
    }
  };

  return (
    <div className="flex h-[85vh] bg-gray-900 text-gray-200 relative overflow-hidden font-sans">
     
      {/* Help Modal */}
      {showHelp && (
        <div className="absolute inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gray-800 border border-teal-500 max-w-sm p-6 rounded shadow-2xl relative text-xs">
            <button onClick={() => setShowHelp(false)} className="absolute top-3 right-3 text-gray-500 hover:text-white"><FiX /></button>
            <h3 className="text-teal-400 font-bold mb-3 flex items-center gap-2 text-sm"><FiHelpCircle /> Editor Guide</h3>
            <p className="mb-4 text-gray-400">Ensure all rows are correctly configured. Preview your changes to unlock the Approve button.</p>
            <button onClick={() => setShowHelp(false)} className="w-full mt-4 bg-teal-600 py-2 rounded font-bold hover:bg-teal-500">Get Started</button>
          </div>
        </div>
      )}
      {successMessage && (
        <div className="absolute inset-0 z-50 backdrop-blur-sm flex items-center justify-center mb-4 p-3 rounded bg-black/70 border border-teal-700 text-teal-400">
                    <div className="bg-gray-900/30 border border-teal-500 max-w-sm p-6 rounded shadow-2xl relative text-sm">
          {successMessage}</div>
        </div>
      )}
      {/* Left Pane: Preview (Includes Approve on Mobile) */}
      <div className={`${viewMode === 'edit' ? 'hidden lg:flex' : 'flex'} w-full lg:w-[30%] min-w-[340px] border border-gray-700 rounded flex-col overflow-hidden bg-black/20`}>
        <div className="p-2 text-xs font-bold bg-gray-800 text-gray-400 flex justify-between items-center">
          <div className="flex items-center gap-2">
            {viewMode === 'preview' && (
              <button onClick={() => setViewMode('edit')} className="lg:hidden text-teal-400 flex items-center gap-1 font-bold">
                <FiArrowLeft /> Back to Editor
              </button>
            )}
            <span>Preview</span>
          </div>
          {generatedUrl && (
            hasPreviewed ? <span className="text-[10px] text-teal-500 flex items-center gap-1"><FiCheck /> Sync</span> : <span className="text-[10px] text-orange-400 flex items-center gap-1"><FiAlertCircle /> Outdated</span>
          )}
        </div>
        
        <div className="relative flex-1 overflow-auto space-y-8 custom-scrollbar bg-gray-900/50">
          {generatedUrl ? (<>
            <Document file={generatedUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)} className="flex flex-col items-center gap-1">
              {Array.from(new Array(numPages), (_, index) => (
                <div ref={(el) => (pageRefs.current[index + 1] = el)} key={`page_${index + 1}`} className="relative shadow-xl border-gray-800 bg-white">
                  {renderCrossHair(index + 1)}
                  <Page onLoadSuccess={(page) => handlePageLoadSuccess(page, index + 1 ) } pageNumber={index + 1} renderTextLayer={false} renderAnnotationLayer={false} width={320} />
                  <span className="absolute italic bottom-2 right-2 text-[8px] text-gray-500">Page {index + 1}</span>
                </div>
              ))}
            </Document></>
          ) : (
            <>
            <div className="h-full flex items-center justify-center text-gray-600 italic text-sm">Preview required</div>
            </>
          )}
        </div>

        {/* MOBILE ONLY APPROVAL AREA */}
        <div className="lg:hidden">
          {hasPreviewed && <ApprovalControls isMobile={true} filename={fileName} updateFileName={(fn) => {setFileName(fn)}} handleApprove={handleApprove} canApprove={canApprove}/>}
        </div>
      </div>

      {/* Right Pane: Table Editor */}
      <div className={`${viewMode === 'preview' ? 'hidden lg:flex' : 'flex'} flex-1 flex flex-col min-w-0`}>
        <div className="flex justify-between items-center mb-1 px-1">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold tracking-wider text-gray-200">Fields</h2>
            <button onClick={() => setShowHelp(true)} className="text-gray-500 hover:text-teal-400 transition-colors"><FiHelpCircle size={18} /></button>
          </div>
          
          <div className="flex gap-2">
            <button onClick={onCancel} className="px-3 py-1 border border-gray-600 rounded text-sm hover:bg-gray-800 transition-colors text-gray-300">Cancel</button>
            <button onClick={addRow} className="flex items-center gap-1 bg-teal-700 text-white hover:bg-teal-600 px-3 py-1 rounded text-sm transition-colors shadow-md font-medium">
              <FiPlus /> Add Row
            </button>
          </div>
        </div>

        <div className="border border-gray-700 rounded overflow-auto flex-1 bg-gray-800/50" style={{ contain: 'content' }}>
          <table className="min-w-full text-[12px] table-fixed">
            <thead className="bg-gray-900 sticky top-0 z-10 shadow-md">
              <tr className="text-gray-300 uppercase text-[10px]">
                {["Page","Type","X","Y","Value","Size","Path","Color","Scale",""].map(col => (
                  <th key={col} className="px-3 py-2 border-b border-gray-700 text-left font-medium">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {rows.map((row, rowIndex) => {
                const isText = row.Type === "TEXT";
                const isImage = row.Type === "IMAGE";
                return (
                  <tr key={rowIndex} className="transition-colors bg-orange-900/10 hover:bg-gray-800/80">
                    <td className="p-1 border-r border-gray-700">
                      <input type="number" value={row.Page} onChange={e => {
                             updateCell(rowIndex,"Page",e.target.value);
                             scrollToPage(e.target.value);
                             setCurrXY({ X: XPage(row.Page, row.X, isImage ? "dpi" : "mm" ), Y: YPage(row.Page, row.Y ,isImage ? "dpi" : "mm" ) } );
                            }}
                           onClick={e => {
                                scrollToPage(e.target.value);
                                setCurrXY({ X: XPage(row.Page, row.X, isImage ? "dpi" : "mm" ), Y: YPage(row.Page, row.Y ,isImage ? "dpi" : "mm" ) } );
                           }} 
                          className="w-10 bg-transparent text-white outline-none px-1" min={1} max={numPages} />
                    </td>
                    <td className="p-1 border-r border-gray-700">
                      <select value={row.Type} onChange={e => {
                                   updateCell(rowIndex,"Type",e.target.value);
                                   scrollToPage(row.Page);
                                   setCurrXY({ X: XPage(row.Page, row.X, isImage ? "dpi" : "mm" ), Y: YPage(row.Page, row.Y ,isImage ? "dpi" : "mm" ) } );         
                                }} 
                          className="bg-gray-700 text-white outline-none rounded-sm px-1">
                        <option value="TEXT">TEXT</option>
                        <option value="IMAGE">IMAGE</option>
                      </select>
                    </td>
                    <td className="p-1 border-r border-gray-700">
                      <div className="flex items-center">
                        <input type="number" value={row.X} onChange={e => {
                            updateCell(rowIndex, "X", e.target.value);
                            scrollToPage(row.Page);
                            setCurrXY({ X: XPage(row.Page, e.target.value, isImage ? "dpi" : "mm" ), Y: YPage(row.Page, row.Y ,isImage ? "dpi" : "mm" ) } );
                        }}
                        onClick={e => { 
                           scrollToPage(row.Page);
                           setCurrXY({ X: XPage(row.Page, e.target.value, isImage ? "dpi" : "mm" ),Y: YPage(row.Page, row.Y ,isImage ? "dpi" : "mm" ) } );
                        }}
                        className="w-12 bg-transparent text-white outline-none px-1 font-mono" />
                        <span className="text-[9px] text-gray-200 italic">{isImage ? "dpi" : "mm"}</span>
                      </div>
                    </td>
                    <td className="p-1 border-r border-gray-700">
                      <div className="flex items-center">
                        <input type="number" value={row.Y} onChange={e => { 
                               updateCell(rowIndex, "Y", e.target.value);
                               scrollToPage(row.Page);
                               setCurrXY({ X: XPage(row.Page, row.X, isImage ? "dpi" : "mm" ), Y: YPage(row.Page, e.target.value ,isImage ? "dpi" : "mm" ) } );
                            }}
                          onClick={e => {
                             scrollToPage(row.Page);
                             setCurrXY( { X :XPage(row.Page, row.X, isImage ? "dpi" : "mm" ),Y: YPage(row.Page, e.target.value ,isImage ? "dpi" : "mm" ) } );
                            }} 
                          className="w-12 bg-transparent text-white outline-none px-1 font-mono" />
                        <span className="text-[9px] text-gray-200 italic">{isImage ? "dpi" : "mm"}</span>
                      </div>
                    </td>
                    <td className={`p-1 border-r border-gray-700 ${!isText ? 'bg-black/40' : ''}`}>
                      <input value={row.Value} disabled={!isText} onChange={e => updateCell(rowIndex,"Value",e.target.value)} className={`w-full bg-transparent text-white outline-none px-1`} />
                    </td>
                    <td className={`p-1 border-r border-gray-700 ${!isText ? 'bg-black/40' : ''}`}>
                      <input type="number" value={row.FontSize} disabled={!isText} onChange={e => updateCell(rowIndex,"FontSize",e.target.value)} className={`w-10 bg-transparent text-white outline-none px-1`} />
                    </td>
                    <td className={`p-1 border-r border-gray-700 ${!isImage ? 'bg-black/40' : ''}`}>
                      <input value={row.ImagePath} disabled={!isImage} onChange={e => updateCell(rowIndex,"ImagePath",e.target.value)} className={`w-full bg-transparent text-white outline-none px-1 text-[10px]`} />
                    </td>
                    <td className={`p-1 border-r border-gray-700 text-center ${!isText ? 'bg-black/40' : ''}`}>
                      <input type="color" value={row.TEXTCOLOR || "#000000"} disabled={!isText} onChange={e => updateCell(rowIndex,"TEXTCOLOR",e.target.value)} className={`w-5 h-5 bg-transparent cursor-pointer`} />
                    </td>
                    <td className={`p-1 border-r border-gray-700 text-center ${!isImage ? 'bg-black/40' : ''}`}>
                      <input type="number" step="0.1" value={row.Scale} disabled={!isImage} onChange={e => updateCell(rowIndex,"Scale",e.target.value)} className={`w-12 bg-transparent text-white outline-none px-1`} />
                    </td>
                    <td className="p-1 text-center">
                      <button onClick={() => removeRow(rowIndex)} className="text-red-500 hover:text-red-400 transition-colors"><FiTrash size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer: Dedicated to Preview & (on Desktop) Approve */}
        <div className="flex flex-col mt-4 gap-4 flex-none border-t border-gray-800 pt-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="sm-px-0 px-1 text-[10px] italic">
              {!isFormValid ? <span className="text-red-400">⚠️ All fields must be filled.</span> : !hasPreviewed ? <span className="text-orange-400">⚠️ Preview changes to continue.</span> : <span className="text-teal-500 font-medium">✅ Preview up-to-date.</span>}
            </div>

            <div className="flex gap-3 w-full sm:w-auto items-center">
              {!hasPreviewed && <button 
                disabled={!isFormValid} 
                onClick={handlePreviewTemplate} 
                className={`px-6 py-1.5 rounded text-sm font-bold flex-1 sm:flex-none transition-all ${isFormValid ? "bg-teal-600 text-white hover:bg-teal-500 shadow-lg" : "bg-gray-700 text-gray-500 cursor-not-allowed"}`}
              >
                Preview
              </button>}

              {/* DESKTOP ONLY APPROVAL AREA */}
              <div className="hidden lg:block">
                {hasPreviewed && <ApprovalControls isMobile={false} filename={fileName} updateFileName={(fn) => {setFileName(fn)}} handleApprove={handleApprove} canApprove={canApprove}/>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}